import {
  queryOptions,
  skipToken,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
  type QueryClient,
} from '@tanstack/react-query';
import type { operations, SegmentDesc, SegmentsGetSegmentsDefaultResponse } from '@customerio/hydra-ui-api';
import { hydraUiApi } from '@/services/api';
import type { QueryConfig } from '../shared/react-query-types';
import { pollWhileUnfinished, useSegmentCountPolling, type SegmentCountStatus } from '../shared/segment-count-polling';
import { requireWorkspaceId } from '../shared/workspace';
import { clampImportsPage, getSegmentImports } from '../import/api';
import { isImportPending, type ImportRecord } from '../import/entity';
import { getSegmentSqlImportRows } from '../sql-import-row/queries';
import { sqlImportRowIsPending, type SqlImportRow } from '../sql-import-row/entity';
import { parseSegment, segmentKeys, type Segment } from './entity';

const STALE_TIME = 60_000;

/** Sentinel key outside the workspace namespace; `skipToken` keeps it empty. */
const INACTIVE_COUNT_STATUS_KEY = ['segment', 'count-status', 'inactive'] as const;

/** The unpaginated segments list plus the workspace's tag definitions. */
export interface SegmentsList {
  segments: Segment[];
  /** Tag definitions referenced by `Segment.tag_ids` (id is numeric on the wire). */
  tags: ReadonlyArray<{ id: number; name: string }>;
}

/**
 * Workspace segments list. The endpoint returns one of three shapes by query
 * mode; with no query params it's the default (full, unpaginated) response.
 * Each row is sideloaded into its detail slot so a follow-up `useSegmentOptional`
 * resolves from cache.
 *
 * @public
 */
export async function getSegments(workspaceId: number, qc: QueryClient): Promise<SegmentsList> {
  // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
  const raw = (await hydraUiApi.segments.list({
    params: { path: { environment_id: String(workspaceId) } },
  })) as SegmentsGetSegmentsDefaultResponse;
  const segments = (raw.segments ?? []).map(parseSegment);
  for (const segment of segments) {
    qc.setQueryData(segmentKeys.detail(workspaceId, segment.id), segment);
  }
  return { segments, tags: raw.tags ?? [] };
}

/** @public */
export const getSegmentsQueryOptions = (workspaceId: number, qc: QueryClient) =>
  queryOptions({
    queryKey: segmentKeys.list(workspaceId, undefined),
    queryFn: () => getSegments(workspaceId, qc),
    // Ember re-queries the list on every visit ("always grab the latest"), so the
    // list must never serve a stale cache — membership, counts and archived state
    // change server-side (builds finishing, bulk actions elsewhere).
    staleTime: 0,
  });

/**
 * The segments linked to any of `formIds`, archived ones included. The filter
 * takes one id, so it's one read per form.
 *
 * @public
 */
export async function getFormSegments(workspaceId: number, formIds: readonly string[]): Promise<Segment[]> {
  const responses = await Promise.all(
    formIds.map((formId) =>
      hydraUiApi.segments.list({
        params: { path: { environment_id: String(workspaceId) }, query: { linked_form_id: formId } },
      }),
    ),
  );
  // A linked-form read answers in the full shape; only `short=1` leaves the rows' details out.
  return responses.flatMap((raw) => ('tags' in raw ? raw.segments : [])).map(parseSegment);
}

/** @public */
export const getFormSegmentsQueryOptions = (workspaceId: number, formIds: readonly string[]) =>
  queryOptions({
    queryKey: segmentKeys.linkedToForms(workspaceId, formIds),
    queryFn: () => getFormSegments(workspaceId, formIds),
    // Like the full list: a segment is archived or renamed on its own page.
    staleTime: 0,
  });

/**
 * A segment as the short list describes it: what a picker needs to name it and
 * mark its kind, whether its conditions read relationships, and whether its
 * related objects have been counted.
 */
export type ShortSegment = Pick<SegmentDesc, 'id' | 'name' | 'type'> & {
  hasRelationshipConditions: boolean;
  hasObjectCounts: boolean;
};

/**
 * Every segment a reader can pick, in one response and in no particular
 * order: the short descriptions `short=1` asks for. The server leaves out
 * archived, hidden and temporary segments, so a caller filters nothing.
 */
export async function getShortSegments(workspaceId: number): Promise<ShortSegment[]> {
  const raw = await hydraUiApi.segments.list({
    params: { path: { environment_id: String(workspaceId) }, query: { short: '1' } },
  });
  return raw.segments.map((row) => ({
    id: row.id,
    name: row.name,
    type: row.type,
    hasRelationshipConditions: row.has_relationship_conditions === true,
    // Read loosely until the synced spec declares the field.
    hasObjectCounts: 'has_object_counts' in row && row.has_object_counts === true,
  }));
}

export const shortSegmentsQueryOptions = (workspaceId: number) =>
  queryOptions({
    queryKey: segmentKeys.shortList(workspaceId),
    queryFn: () => getShortSegments(workspaceId),
    // Ember reads the list afresh each time a picker mounts
    // (`dashboard-page/segments-card/index.gts:92-99`), so a segment created
    // or renamed elsewhere shows the next time the page is opened.
    staleTime: 0,
  });

/** A manual-segment picker row (id normalized to the layer's string ids). */
export interface ManualSegmentOption {
  id: string;
  name: string;
}

const MANUAL_SEGMENT_PAGE_SIZE = 50;

type ManualSegmentSearchQuery = NonNullable<
  operations['get-v1-environments-environment_id-segments']['parameters']['query']
>;

/**
 * Server-side manual-segment search for the add-to-segment picker: first page
 * of unarchived static segments matching the term (name/description/id). An
 * empty term lists the first page unfiltered.
 *
 * @public
 */
export async function searchManualSegments(workspaceId: number, search: string): Promise<ManualSegmentOption[]> {
  const query: ManualSegmentSearchQuery = {
    page: 0,
    size: MANUAL_SEGMENT_PAGE_SIZE,
    type: 'static',
    archived: 'false',
  };
  if (search !== '') {
    query.search = search;
  }
  const raw = await hydraUiApi.segments.list({
    params: { path: { environment_id: String(workspaceId) }, query },
  });
  return raw.segments.map((row) => ({ id: String(row.id), name: row.name }));
}

/**
 * How many manual segments the workspace has.
 *
 * Asked for its own sake rather than counted off a page: the review step opens on "create a new
 * segment" only for a workspace that does not already have a great many, and a page of fifty
 * cannot answer that. The embedded app counts the whole list it fetched, which is the same
 * question asked more expensively.
 *
 * @public
 */
export async function getManualSegmentCount(workspaceId: number): Promise<number> {
  const raw = await hydraUiApi.segments.list({
    params: {
      path: { environment_id: String(workspaceId) },
      // One row, because only the total is wanted; the server reports it whatever the page size.
      query: { page: 0, size: 1, type: 'static', archived: 'false' },
    },
  });

  // Two of the three response shapes carry no pagination; the page falls back to what it holds,
  // which for a size of one is only ever an undercount the caller reads as "not many".
  if ('meta' in raw && 'pagination' in raw.meta) {
    return raw.meta.pagination.total;
  }
  return raw.segments.length;
}

/** @public */
export const manualSegmentCountQueryOptions = (workspaceId: number) =>
  queryOptions({
    queryKey: segmentKeys.manualCount(workspaceId),
    queryFn: () => getManualSegmentCount(workspaceId),
    // Segments are created and archived elsewhere, and the answer only decides a default, so a
    // stale one is cheap — but never wrong for a whole session.
    staleTime: 60_000,
  });

/** @public */
export const searchManualSegmentsQueryOptions = (workspaceId: number, search: string) =>
  queryOptions({
    queryKey: segmentKeys.manualOptions(workspaceId, search),
    queryFn: () => searchManualSegments(workspaceId, search),
    // Picker options must reflect segments created/renamed elsewhere since the
    // last open — never serve a stale page.
    staleTime: 0,
  });

/** One page of the workspace's segments, with how many there are in all. */
export interface SegmentsPage {
  segments: Segment[];
  total: number;
  /** Tag definitions referenced by `Segment.tag_ids` (id is numeric on the wire). */
  tags: ReadonlyArray<{ id: number; name: string }>;
}

type SegmentsListQuery = NonNullable<operations['get-v1-environments-environment_id-segments']['parameters']['query']>;

/**
 * `page` is 0-based. The filters carry the wire's names; the list filters are sent as JSON arrays,
 * and a filter left out is not sent.
 */
export interface SegmentsPageParams {
  page: number;
  size: number;
  type?: Segment['type'];
  search?: string;
  /** Tag ids; any that is not a number is dropped. */
  searchTagIds?: readonly string[];
  archived?: boolean;
  usage?: SegmentsListQuery['usage'];
  audience_status?: readonly string[];
  audience_type?: readonly string[];
  /** Object type ids; any that is not a number is dropped. */
  objectTypeIds?: readonly string[];
}

/** The ids the server compares as numbers, as a JSON array of them. */
function numericIdsParam(ids: readonly string[] | undefined): string | undefined {
  if (ids == null) return undefined;
  return JSON.stringify(ids.map((id) => Number.parseInt(id, 10)).filter((id) => !Number.isNaN(id)));
}

function listParam(values: readonly string[] | undefined): string | undefined {
  return values == null ? undefined : JSON.stringify(values);
}

function segmentsPageQuery(params: SegmentsPageParams): SegmentsListQuery {
  return {
    page: params.page,
    size: params.size,
    type: params.type,
    search: params.search,
    searchTagIds: numericIdsParam(params.searchTagIds),
    archived: params.archived == null ? undefined : String(params.archived),
    usage: params.usage,
    audience_status: listParam(params.audience_status),
    audience_type: listParam(params.audience_type),
    objectTypeIds: numericIdsParam(params.objectTypeIds),
  };
}

/**
 * Without an archive filter the server lists active segments only.
 *
 * @public
 */
export async function getSegmentsPage(workspaceId: number, params: SegmentsPageParams): Promise<SegmentsPage> {
  const raw = await hydraUiApi.segments.list({
    params: { path: { environment_id: String(workspaceId) }, query: segmentsPageQuery(params) },
  });
  // A page and size select the paginated shape; the others carry no total.
  if (!('meta' in raw && 'pagination' in raw.meta)) {
    throw new Error('segments page response carried no pagination');
  }
  return { segments: raw.segments.map(parseSegment), total: raw.meta.pagination.total, tags: raw.tags };
}

/** @public */
export const segmentsPageQueryOptions = (workspaceId: number, params: SegmentsPageParams) =>
  queryOptions({
    queryKey: segmentKeys.page(workspaceId, params),
    queryFn: () => getSegmentsPage(workspaceId, params),
    // Ember re-queries on every visit; membership changes elsewhere, so never serve a stale page.
    staleTime: 0,
  });

/**
 * The first `size` segments something in the workspace uses. The server applies the usage filter
 * only with a page size. Each row is sideloaded into its detail slot.
 */
export async function getInUseSegments(
  workspaceId: number,
  size: number,
  qc: QueryClient,
  signal?: AbortSignal,
): Promise<Segment[]> {
  const raw = await hydraUiApi.segments.list({
    params: { path: { environment_id: String(workspaceId) }, query: { size, usage: 'in_use' } },
    signal,
  });
  // Only `short=1` answers without the rows' details, and this read does not ask for it.
  const segments = 'tags' in raw ? raw.segments.map(parseSegment) : [];
  for (const segment of segments) {
    qc.setQueryData(segmentKeys.detail(workspaceId, segment.id), segment);
  }
  return segments;
}

/**
 * The segments a person belongs to, each with when they entered it (`timestamp`).
 *
 * @public
 */
export async function getCustomerSegments(workspaceId: number, customerId: string): Promise<Segment[]> {
  const raw = await hydraUiApi.segments.getCustomersSegments({
    params: { path: { environment_id: String(workspaceId), customer_id: customerId } },
  });
  return raw.segments.map(parseSegment);
}

/** @public */
export const customerSegmentsQueryOptions = (workspaceId: number, customerId: string) =>
  queryOptions({
    queryKey: segmentKeys.forCustomer(workspaceId, customerId),
    queryFn: () => getCustomerSegments(workspaceId, customerId),
    // Ember reloads the relationship each time a segments view opens.
    staleTime: 0,
  });

/** @public */
export async function getSegment(workspaceId: number, segmentId: string, qc: QueryClient): Promise<Segment> {
  const raw = await hydraUiApi.segments.get({
    params: { path: { environment_id: String(workspaceId), segment_id: segmentId } },
  });
  if (raw.segment == null) {
    throw new Error(`segment ${segmentId} not found`);
  }
  const parsed = parseSegment(raw.segment);
  qc.setQueryData(segmentKeys.detail(workspaceId, parsed.id), parsed);
  return parsed;
}

/** @public */
export const getSegmentQueryOptions = (workspaceId: number, segmentId: string, qc: QueryClient) =>
  queryOptions({
    queryKey: segmentKeys.detail(workspaceId, segmentId),
    queryFn: () => getSegment(workspaceId, segmentId, qc),
    staleTime: STALE_TIME,
  });

interface UseSegmentOptionalOptions {
  workspaceId: number;
  id: string;
  enabled?: boolean;
}

/**
 * Non-suspense, conditionally-enabled by-id hook (see `useSnippetOptional`).
 * `skipToken` keeps the slot unregistered while inactive; `retry: false` so a
 * 404 surfaces immediately for deleted-resource detection.
 *
 * @public
 */
export const useSegmentOptional = ({ workspaceId, id, enabled = true }: UseSegmentOptionalOptions) => {
  const qc = useQueryClient();
  const active = enabled && workspaceId > 0 && id !== '';
  return useQuery({
    queryKey: active ? segmentKeys.detail(workspaceId, id) : ['segment', 'inactive'],
    queryFn: active ? () => getSegment(workspaceId, id, qc) : skipToken,
    staleTime: STALE_TIME,
    retry: false,
  });
};

/* ------------------------------------------------------------------------- */
/* Detail-tab reads: count / metrics / status / usage                         */
/* ------------------------------------------------------------------------- */

/** Live member count (unwraps `{ count }`). Ember's `loadPeopleCount()`. @public */
export async function getSegmentCount(workspaceId: number, segmentId: string): Promise<number> {
  const res = await hydraUiApi.segments.getCount({
    params: { path: { environment_id: String(workspaceId), segment_id: segmentId } },
  });
  return res.count;
}

/** @public */
export const getSegmentCountQueryOptions = (workspaceId: number, segmentId: string) =>
  queryOptions({
    queryKey: segmentKeys.count(workspaceId, segmentId),
    queryFn: () => getSegmentCount(workspaceId, segmentId),
    // Live count — Ember refetched on demand; keep it fresh.
    staleTime: 0,
  });

/** @public */
export const useSegmentCount = ({
  segmentId,
  queryConfig,
}: {
  segmentId: string;
  queryConfig?: QueryConfig<typeof getSegmentCountQueryOptions>;
}) => {
  const workspaceId = requireWorkspaceId();
  return useSuspenseQuery({ ...getSegmentCountQueryOptions(workspaceId, segmentId), ...queryConfig });
};

/**
 * Live count with its settled flag, where `getSegmentCount` unwraps to the
 * number alone. Membership is computed in the background, so a caller that
 * polls needs `finished` to know whether to ask again.
 *
 * @public
 */
export async function getSegmentCountStatus(workspaceId: number, segmentId: string): Promise<SegmentCountStatus> {
  const res = await hydraUiApi.segments.getCount({
    params: { path: { environment_id: String(workspaceId), segment_id: segmentId } },
  });
  return { count: res.count, finished: res.finished };
}

/** Distinct from `segmentKeys.count`, whose cached value is the bare number. @public */
export const getSegmentCountStatusQueryOptions = (workspaceId: number, segmentId: string) =>
  queryOptions({
    queryKey: segmentKeys.countStatus(workspaceId, segmentId),
    queryFn: () => getSegmentCountStatus(workspaceId, segmentId),
    staleTime: 0,
    refetchInterval: pollWhileUnfinished,
  });

/**
 * Non-suspending read of a segment's count, polled until it settles.
 *
 * Never suspends: the trigger panel is already on screen when the count is
 * wanted, so it shows its waiting line rather than blanking. Pass
 * `segmentId: null` until the backing segment exists — the request is skipped
 * rather than fired against a missing id.
 *
 * @public
 */
export const useSegmentCountStatusOptional = ({
  segmentId,
  enabled = true,
}: {
  segmentId: string | null;
  enabled?: boolean;
}) =>
  useSegmentCountPolling({
    segmentId,
    enabled,
    inactiveKey: INACTIVE_COUNT_STATUS_KEY,
    countKey: segmentKeys.countStatus,
    fetchCount: getSegmentCountStatus,
  });

/** @public */
export async function getSegmentMetrics(workspaceId: number, segmentId: string) {
  return hydraUiApi.segments.getMetrics({
    params: { path: { environment_id: String(workspaceId), segment_id: segmentId } },
  });
}

/** @public */
export const getSegmentMetricsQueryOptions = (workspaceId: number, segmentId: string) =>
  queryOptions({
    queryKey: segmentKeys.metrics(workspaceId, segmentId),
    queryFn: () => getSegmentMetrics(workspaceId, segmentId),
    // Ember refetches metrics on every revisit — keep them fresh, not cached 60s.
    staleTime: 0,
  });

/** @public */
export const useSegmentMetrics = ({
  segmentId,
  queryConfig,
}: {
  segmentId: string;
  queryConfig?: QueryConfig<typeof getSegmentMetricsQueryOptions>;
}) => {
  const workspaceId = requireWorkspaceId();
  return useSuspenseQuery({ ...getSegmentMetricsQueryOptions(workspaceId, segmentId), ...queryConfig });
};

/** Build status. @public */
export async function getSegmentStatus(workspaceId: number, segmentId: string) {
  return hydraUiApi.segments.getStatus({
    params: { path: { environment_id: String(workspaceId), segment_id: segmentId } },
  });
}

/** @public */
export const getSegmentStatusQueryOptions = (workspaceId: number, segmentId: string) =>
  queryOptions({
    queryKey: segmentKeys.status(workspaceId, segmentId),
    queryFn: () => getSegmentStatus(workspaceId, segmentId),
    // Reflects an in-progress build; callers typically poll via refetchInterval.
    staleTime: 0,
  });

/** @public */
export const useSegmentStatus = ({
  segmentId,
  queryConfig,
}: {
  segmentId: string;
  queryConfig?: QueryConfig<typeof getSegmentStatusQueryOptions>;
}) => {
  const workspaceId = requireWorkspaceId();
  return useSuspenseQuery({ ...getSegmentStatusQueryOptions(workspaceId, segmentId), ...queryConfig });
};

/** Per-segment usage (what references this segment). @public */
export async function getSegmentUsage(workspaceId: number, segmentId: string) {
  return hydraUiApi.segments.getUsage({
    params: { path: { environment_id: String(workspaceId), segment_id: segmentId } },
  });
}

/** @public */
export const getSegmentUsageQueryOptions = (workspaceId: number, segmentId: string) =>
  queryOptions({
    queryKey: segmentKeys.usage(workspaceId, segmentId),
    queryFn: () => getSegmentUsage(workspaceId, segmentId),
    // Usage reflects live references (campaigns/newsletters/…); Ember refetches it.
    staleTime: 0,
  });

/** @public */
export const useSegmentUsage = ({
  segmentId,
  queryConfig,
}: {
  segmentId: string;
  queryConfig?: QueryConfig<typeof getSegmentUsageQueryOptions>;
}) => {
  const workspaceId = requireWorkspaceId();
  return useSuspenseQuery({ ...getSegmentUsageQueryOptions(workspaceId, segmentId), ...queryConfig });
};

/**
 * Bulk usage map, keyed by segment id — the list page's pattern (Ember fetches
 * the list with `usage_info: 0`, then hydrates usage separately via
 * `segments_usage?segment_ids=…`).
 *
 * @public
 */
export async function getSegmentsUsage(workspaceId: number, segmentIds: string[]) {
  return hydraUiApi.segments.getSegmentsUsage({
    params: { path: { environment_id: String(workspaceId) }, query: { segment_ids: segmentIds.join(',') } },
  });
}

/** @public */
export const getSegmentsUsageQueryOptions = (workspaceId: number, segmentIds: string[]) =>
  queryOptions({
    queryKey: segmentKeys.usageBulk(workspaceId, segmentIds),
    queryFn: () => getSegmentsUsage(workspaceId, segmentIds),
    // List-page usage hydration — Ember refetches on every revisit alongside the list.
    staleTime: 0,
  });

/** @public */
export const useSegmentsUsage = ({
  segmentIds,
  queryConfig,
}: {
  segmentIds: string[];
  queryConfig?: QueryConfig<typeof getSegmentsUsageQueryOptions>;
}) => {
  const workspaceId = requireWorkspaceId();
  return useSuspenseQuery({ ...getSegmentsUsageQueryOptions(workspaceId, segmentIds), ...queryConfig });
};

/* ------------------------------------------------------------------------- */
/* Imports into a manual segment                                             */
/* ------------------------------------------------------------------------- */

/**
 * Whether a CSV import or a SQL import run into the segment is still moving: transforming, queued
 * for ingress, or mid-ingress. Only the first page of CSV imports is read; the newest come first, and
 * an import still running is never far down the list. The page is named rather than left to the
 * endpoint's fallback, so the bound holds on its own.
 *
 * @public
 */
export async function getSegmentHasImportsInProgress(workspaceId: number, segmentId: string): Promise<boolean> {
  const path = { environment_id: String(workspaceId) };
  const [csv, sql] = await Promise.all([
    hydraUiApi.imports.list({
      params: { path, query: { type: 'segment', segment_id: Number(segmentId), page: clampImportsPage(1) } },
    }),
    hydraUiApi.sqlImports.getSqlImportRows({ params: { path, query: { segment_id: segmentId } } }),
  ]);
  return csv.imports.some(isImportPending) || sql.sql_import_rows.some(sqlImportRowIsPending);
}

const IMPORTS_POLL_MS = 4000;

/** Rereads while an import is still moving; a settled answer needs no more reads. */
export function pollWhileImporting(query: { state: { data: boolean | undefined } }): number | false {
  return query.state.data === true ? IMPORTS_POLL_MS : false;
}

/**
 * Polled while an import runs. When the last one settles, the import has changed who is in the
 * segment, so its record and count are read again.
 *
 * @public
 */
export const getSegmentImportsInProgressQueryOptions = (workspaceId: number, segmentId: string, qc: QueryClient) =>
  queryOptions({
    queryKey: segmentKeys.importsInProgress(workspaceId, segmentId),
    queryFn: async () => {
      const wasImporting = qc.getQueryData<boolean>(segmentKeys.importsInProgress(workspaceId, segmentId)) === true;
      const isImporting = await getSegmentHasImportsInProgress(workspaceId, segmentId);
      if (wasImporting && !isImporting) {
        void qc.invalidateQueries({ queryKey: segmentKeys.detail(workspaceId, segmentId) });
        void qc.invalidateQueries({ queryKey: segmentKeys.count(workspaceId, segmentId) });
      }
      return isImporting;
    },
    staleTime: 0,
    refetchInterval: pollWhileImporting,
  });

/** Everything imported into a manual segment: its CSV imports and the SQL import runs that wrote to it. */
export type SegmentImportHistory = {
  csv: ImportRecord[];
  sql: SqlImportRow[];
};

/** @public */
export async function getSegmentImportHistory(workspaceId: number, segmentId: string): Promise<SegmentImportHistory> {
  const [csv, sql] = await Promise.all([
    getSegmentImports(workspaceId, segmentId),
    getSegmentSqlImportRows(workspaceId, segmentId),
  ]);
  return { csv, sql };
}

/** Whether a CSV import in the history is still moving, which is what the history is reread for. */
export function hasPendingCsvImport(history: SegmentImportHistory | undefined): boolean {
  return history?.csv.some(isImportPending) === true;
}

/**
 * Rereads while a CSV import is still moving. A failed read stops it, as the history then shows
 * its error with a Retry that reads, and so polls, again.
 */
export function pollWhileCsvImporting(query: {
  state: { data: SegmentImportHistory | undefined; status: 'pending' | 'error' | 'success' };
}): number | false {
  if (query.state.status === 'error') return false;
  return hasPendingCsvImport(query.state.data) ? IMPORTS_POLL_MS : false;
}

/**
 * A manual segment's import history, read afresh on every visit and polled while a CSV import
 * runs. When the last one settles it has changed who is in the segment, so the segment and its
 * count are read again.
 *
 * @public
 */
export const getSegmentImportHistoryQueryOptions = (workspaceId: number, segmentId: string, qc: QueryClient) =>
  queryOptions({
    queryKey: segmentKeys.importHistory(workspaceId, segmentId),
    queryFn: async () => {
      const previous = qc.getQueryData<SegmentImportHistory>(segmentKeys.importHistory(workspaceId, segmentId));
      const history = await getSegmentImportHistory(workspaceId, segmentId);
      if (hasPendingCsvImport(previous) && !hasPendingCsvImport(history)) {
        void qc.invalidateQueries({ queryKey: segmentKeys.detail(workspaceId, segmentId) });
        void qc.invalidateQueries({ queryKey: segmentKeys.count(workspaceId, segmentId) });
      }
      return history;
    },
    staleTime: 0,
    refetchInterval: pollWhileCsvImporting,
  });
